import React, { useEffect, useMemo, useRef, useState } from "react";
import {  InputAdornment, Tooltip, Typography } from "@mui/material";
import { Add as AddIcon, Search as SearchIcon } from "@mui/icons-material";
import {  SortAsc, SortDesc} from "lucide-react";

// Components
import EpisodeItem from "./common/EpisodeItem";

// Utils
import { DebouncedCommit, createDebouncedCommit } from "./common/debouncedCommit";
import { RUNS_SEARCH_DEBOUNCE_MS, RunsFilter, RunsSlice } from "./common/runsQuery";

// Types
import { SeriesSerializer, SeriesType, ShareRunMapData } from "./types/MeGenTypes";

// Styles
import {
  CreateButton,
  EmptyState,
  LoadMoreButton,
  ScrollableList,
  SearchInput,
  SortButton,
  ToolbarRow,
} from "./styles/ProjectDetail";

// ─── Props ────────────────────────────────────────────────

type CardProjectDetailVideosProps = {
  /** ผลค้น/แบ่งหน้าที่มาจาก server — ไม่ต้องกรองซ้ำในนี้ */
  runs: RunsSlice;
  onSearchRuns?: (filter: RunsFilter, mode?: "replace" | "append") => void;
  seriesList: SeriesSerializer[];
  isRefreshing?: boolean;
  isJustUpdated?: boolean;
  onCreateVdoFromScript?: (seriesId?: number) => void;
  onSelectEpisode?: (episodeId: string, runType?: SeriesType) => void;
  onRestartEpisode?: (episodeId: string) => void;
  onDuplicateEpisode?: (episodeId: string) => void;
  onDeleteEpisode?: (episodeId: string) => void;
  onShareEpisode?: (shareData: ShareRunMapData) => void;
};

// ─── Component ────────────────────────────────────────────

const CardProjectDetailVideos: React.FC<CardProjectDetailVideosProps> = (props) => {

  const { q, sort, items, hasMore, loading } = props.runs;

  // ข้อความในช่องค้น เก็บแยกจาก state กลาง เพื่อให้พิมพ์ลื่นโดยไม่ต้องรอ round-trip
  const [text, setText] = useState(q);

  // ref กัน closure เก่าค้างใน debounce ที่สร้างครั้งเดียว
  const searchRef = useRef(props.onSearchRuns);
  const sortRef = useRef(sort);
  searchRef.current = props.onSearchRuns;
  sortRef.current = sort;

  const commitRef = useRef<DebouncedCommit<string> | null>(null);
  if (!commitRef.current) {
    commitRef.current = createDebouncedCommit<string>(
      (value) => searchRef.current?.({ q: value, sort: sortRef.current }, "replace"),
      RUNS_SEARCH_DEBOUNCE_MS
    );
  }
  const debounced = commitRef.current;

  // ออกจากหน้าไปแล้วไม่ต้องยิงค้นหาที่ค้างอยู่ (ต่างจากช่องกรอกราคาที่ต้อง flush เพื่อไม่ให้ค่าหาย)
  useEffect(() => () => debounced.cancel(), [debounced]);

  // sync คำค้นจาก state กลาง (เช่นตอนเปลี่ยนโปรเจกต์) — แต่ห้ามทับขณะผู้ใช้กำลังพิมพ์ค้างอยู่
  useEffect(() => {
    if (!debounced.hasPending()) setText(q);
  }, [q, debounced]);

  const handleToggleSort = () => {
    // ส่งคำค้นที่เห็นบนจอไปพร้อมลำดับใหม่ แล้วยกเลิกตัวที่ค้าง ไม่ให้ยิงซ้ำด้วย sort เก่า
    debounced.cancel();
    searchRef.current?.({ q: text.trim(), sort: sort === "newest" ? "oldest" : "newest" }, "replace");
  };

  // ใช้ค่าที่ commit แล้ว ไม่ใช่ค่าในช่องพิมพ์ — หน้าถัดไปต้องต่อจากผลชุดที่แสดงอยู่
  const handleLoadMore = () => searchRef.current?.({ q, sort }, "append");

  const seriesMap = useMemo(() => {
    return props.seriesList.reduce((acc, s) => {
      if (s.id && s.id != null) acc[s.id] = s.name;
      return acc;
    }, {} as Record<number, string>);
  }, [props.seriesList]);

  // -- default series for default script2vdo
  const defaultScriptToVideoSeriesId = useMemo(() => {
    const scriptToVideoSeries = props.seriesList
      .filter(s => s.series_type === "script2vdo" && s.id != null)
      .sort((a, b) => a.id! - b.id!);

    return scriptToVideoSeries[0]?.id ?? undefined;
  }, [props.seriesList]);

  return (
    <>
      {/* Toolbar */}
      <ToolbarRow id="megen-videos-toolbar">
        <SearchInput
          id="megen-videos-search-input"
          placeholder="Search videos..."
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            debounced.push(e.target.value);
          }}
          onBlur={() => debounced.flush()}
          onKeyDown={(e) => {
            if (e.key === "Enter") debounced.flush();
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#94a3b8", fontSize: "1.25rem" }} />
              </InputAdornment>
            ),
          }}
        />

        <Tooltip title={sort === "newest" ? "Newest First" : "Oldest First"}>
          <SortButton id="megen-videos-sort-btn" onClick={handleToggleSort}>
            {sort === "newest" ? <SortDesc size={18} /> : <SortAsc size={18} />}
          </SortButton>
        </Tooltip>

        <CreateButton
          id="megen-videos-create-btn"
          startIcon={<AddIcon />}
          onClick={() => props.onCreateVdoFromScript?.(defaultScriptToVideoSeriesId)}
        >
          New Video
        </CreateButton>
      </ToolbarRow>

      {/* List */}
      {items.length === 0 ? (
        <EmptyState id="megen-videos-empty-state">
          {loading ? (
            <Typography sx={{ fontSize: "1rem" }}>กำลังค้นหา…</Typography>
          ) : q ? (
            <Typography sx={{ fontSize: "1rem" }}>
              No videos found matching "{q}".
            </Typography>
          ) : (
            <>
              <Typography sx={{ fontSize: "1.1rem", marginBottom: "0.5rem" }}>
                ยังไม่มี Video ที่คุณสร้างไว้
              </Typography>
              <Typography sx={{ fontSize: "0.9rem", color: "#cbd5e1" }}>
                กด Create New VDO เพื่อเริ่มต้น
              </Typography>
            </>
          )}
        </EmptyState>
      ) : (
        <ScrollableList
          id="megen-videos-list"
          sx={props.isJustUpdated ? {
            "@keyframes megenFadeIn": {
              from: { opacity: 0.4, transform: "translateY(4px)" },
              to: { opacity: 1, transform: "translateY(0)" },
            },
            animation: "megenFadeIn 0.4s ease",
          } : undefined}
        >
          {items.map((run) => (
            <EpisodeItem
              key={run.id}
              episode={run}
              seriesName={run.series_id ? seriesMap[run.series_id] : undefined}
              onSelect={props.onSelectEpisode}
              onRestart={props.onRestartEpisode}
              onDuplicate={props.onDuplicateEpisode}
              onDelete={props.onDeleteEpisode}
              onShare={props.onShareEpisode}
            />
          ))}

          {hasMore && (
            <LoadMoreButton
              id="megen-videos-load-more-btn"
              onClick={handleLoadMore}
              disabled={loading}
            >
              {loading ? "กำลังโหลด…" : `โหลดเพิ่ม (แสดง ${items.length} จาก ${props.runs.total})`}
            </LoadMoreButton>
          )}
        </ScrollableList>
      )}
    </>
  );
};

export default CardProjectDetailVideos;